<template>
  <b-modal
    ref="lastAccessModal"
    id="last-access-modal"
    v-model="isVisible"
    :title="$t('last_access.title')"
    size="md"
    centered
    @hide="close"
  >
    <div v-if="lastAccess" class="access-details">
      <div class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="ClockIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.date_time') }}:</strong>
        </div>
        <span class="detail-value">{{
          formatDateTime(lastAccess.login_at)
        }}</span>
      </div>
      <div class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="GlobeIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.ip') }}:</strong>
        </div>
        <span class="detail-value">{{ lastAccess.ip_address || '--' }}</span>
      </div>
      <div class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="MonitorIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.browser') }}:</strong>
        </div>
        <span class="detail-value">{{ lastAccess.browser || '--' }}</span>
      </div>
      <div class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="LayersIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.platform') }}:</strong>
        </div>
        <span class="detail-value">{{ lastAccess.platform || '--' }}</span>
      </div>
      <div class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="SmartphoneIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.device') }}:</strong>
        </div>
        <span class="detail-value">{{
          formatDeviceType(lastAccess.device_type)
        }}</span>
      </div>
      <div v-if="lastAccess.user_agent" class="detail-row">
        <div class="detail-label">
          <feather-icon
            icon="InfoIcon"
            size="18"
            class="detail-icon text-primary"
          />
          <strong>{{ $t('last_access.user_agent') }}:</strong>
        </div>
        <span class="detail-value user-agent">{{ lastAccess.user_agent }}</span>
      </div>
    </div>
    <div v-else class="no-access">
      <p>{{ $t('last_access.no_access_found') }}</p>
    </div>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button variant="primary" @click="close">
          {{ $t('action.close') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BButton, BModal } from 'bootstrap-vue'
import FeatherIcon from '@/components/FeatherIcon.vue'

export default {
  name: 'LastAccessPopup',
  components: {
    BButton,
    BModal,
    FeatherIcon
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    lastAccess: {
      type: Object,
      default: null
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.$refs.lastAccessModal) {
        this.$nextTick(() => {
          this.$refs.lastAccessModal.show()
        })
      } else if (!newVal && this.$refs.lastAccessModal) {
        this.$refs.lastAccessModal.hide()
      }
    }
  },
  mounted() {
    if (this.value && this.$refs.lastAccessModal) {
      this.$nextTick(() => {
        this.$refs.lastAccessModal.show()
      })
    }
  },
  methods: {
    close() {
      this.$emit('input', false)
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '--'
      // Usa o utilitário do projeto com formato: "seg, 13/10/2024 às 12:30:15"
      const format = 'ddd, DD/MM/YYYY [às] HH:mm:ss'
      return this.$utils.format.dateWithFormat(dateStr, format)
    },

    formatDeviceType(deviceType) {
      if (!deviceType) return '--'

      const types = {
        mobile: this.$t('last_access.device_types.mobile'),
        tablet: this.$t('last_access.device_types.tablet'),
        desktop: this.$t('last_access.device_types.desktop')
      }

      return types[deviceType] || deviceType
    }
  }
}
</script>

<style lang="scss">
#last-access-modal {
  z-index: 1060 !important;
}
</style>

<style lang="scss" scoped>
.access-details {
  padding: 0.75rem 0;

  .detail-row {
    display: flex;
    margin-bottom: 1.5rem;
    align-items: center;
    gap: 1rem;
    min-height: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    .detail-label {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      min-width: 180px;
      flex-shrink: 0;

      .detail-icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      strong {
        font-weight: 600;
        color: #5e5873;
        white-space: nowrap;
        line-height: 1.5;
      }
    }

    // Garante que os ícones usem a cor primária da plataforma
    ::v-deep .detail-label .detail-icon svg {
      stroke: var(--bs-primary, var(--primary, #156440)) !important;
    }

    .detail-value {
      flex: 1;
      color: #6e6b7b;
      line-height: 1.5;
      word-break: break-word;

      &.user-agent {
        font-size: 0.8125rem;
        word-break: break-all;
        color: #b4b7bd;
        font-family: 'Courier New', monospace;
        line-height: 1.4;
      }
    }
  }
}

.no-access {
  padding: 2rem;
  text-align: center;
  color: #6e6b7b;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem;

  .btn {
    min-width: 100px;
  }
}
</style>
